import { Form, useNavigation, useParams } from "react-router-dom";

import Button from "components/Button";
import SubmitButton from "components/SubmitButton";
import TaggedText from "components/TaggedText";
import Modal, { ModalButtonsSection, ModalContent, ModalSections } from "v2/components/billing/modals";

import { BillingActions, BillingInputName } from "v2/dataroutes/BillingData";

import { useActiveSubscription } from "v2/hooks/billing/useActiveSubscription";
import { useDiscountPrice } from "v2/hooks/billing/useDiscountPrice";
import { usePlans } from "v2/hooks/billing/usePlans";

import { fmtCurrency, fmtDate } from "utils";
import { isAnnualPlan } from "utils/planUtils";


export function DowngradeSubscriptionModal() {
  const { planId } = useParams() as { planId: string };
  const navigation = useNavigation();
  const { allPlans } = usePlans();
  const { activeSubscription } = useActiveSubscription();

  const targetPlan = allPlans?.find(p => p.planSlug === planId);
  const price = useDiscountPrice({ targetPlan }); // not checking scheduled changes here, manually downgrading to a plan should simply drop all current scheduled changes
  const isOnAnnualPlan = isAnnualPlan(activeSubscription?.planSlug);

  return (
    <Modal headline="Downgrade Subscription" preventClosingIf={ navigation.state !== "idle" }>
      { ({ closeModal }) =>
        <Form method="POST" action="/v2/billing">
          <input type="hidden" { ...BillingActions.downgrade } />
          <input type="hidden" name={ BillingInputName.targetPlanId } value={ planId } />

          <ModalContent>
            <ModalSections>
              <div>
                Are you sure you want to downgrade your subscription to <span className="font-bold capitalize">{ targetPlan?.planName }</span>?
              </div>

              { isOnAnnualPlan && (
                <div>
                  By downgrading your subscription, we will reset your credit counters and
                  invoice <span className="font-bold">{ fmtCurrency(price / 100) }</span> on { fmtDate(new Date((activeSubscription?.currentTermEnd || 0) * 1000)) }, when
                  your annual subscription ends.
                  <TaggedText
                    message=" If you would like to end your annual subscription and downgrade earlier, please [contact our support team|contact_support]."
                    tagCallbacks={ { contact_support: closeModal } }
                  />
                </div>
              ) }

              { !isOnAnnualPlan && (
                <div>
                  By downgrading your subscription, we will reset your credit counters, invoice <span className="font-bold">{ fmtCurrency(price / 100) }</span> and
                  update your billing date either immediately or at the end of current cycle.
                </div>
              ) }

            </ModalSections>

            { isOnAnnualPlan && (
              <ModalButtonsSection>
                <Button
                  text="Cancel"
                  className="button button-secondary"
                  size="SM"
                  onClick={ closeModal }
                />
                <SubmitButton
                  text="Downgrade at End of Cycle"
                  className="button button-primary destructive"
                  size="SM"
                  name={ BillingInputName.immediate }
                  value="false"
                />
              </ModalButtonsSection>
            ) }

            { !isOnAnnualPlan && (
              <div className="flex flex-col items-end gap-y-2 gap-x-1">
                <SubmitButton
                  text="Downgrade at End of Cycle"
                  className="button button-primary destructive"
                  size="SM"
                  name={ BillingInputName.immediate }
                  value="false"
                  showSubmitSpinnerIf={ () => navigation.formData?.get(BillingInputName.immediate) === "false" }
                />
                <div className="flex justify-between w-full">
                  <Button
                    text="Cancel"
                    className="button button-secondary"
                    size="SM"
                    onClick={ closeModal }
                  />
                  <SubmitButton
                    text="Downgrade Immediately"
                    className="button button-primary destructive"
                    size="SM"
                    name={ BillingInputName.immediate }
                    value="true"
                    showSubmitSpinnerIf={ () => navigation.formData?.get(BillingInputName.immediate) === "true" }
                  />
                </div>
              </div>
            ) }
          </ModalContent>
        </Form>
      }
    </Modal>
  )
}
